import React from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import {
  AuthProvider,
  useAuth,
} from "./context/AuthContext";
import { DateRangeProvider } from "./context/DateRangeContext";
import Layout from "./components/layout/Layout";

// Pages — lazy loaded for performance
const Login = React.lazy(() => import("./pages/Login"));
const Dashboard = React.lazy(
  () => import("./pages/admin/Dashboard"),
);
const Production = React.lazy(
  () => import("./pages/admin/Production"),
);
const CostLines = React.lazy(
  () => import("./pages/admin/CostLines"),
);
const Pricing = React.lazy(
  () => import("./pages/admin/Pricing"),
);
const Inventory = React.lazy(
  () => import("./pages/admin/Inventory"),
);
const Orders = React.lazy(
  () => import("./pages/admin/Orders"),
);
const Customers = React.lazy(
  () => import("./pages/admin/Customers"),
);
const Delivery = React.lazy(
  () => import("./pages/admin/Delivery"),
);
const Reports = React.lazy(
  () => import("./pages/admin/Reports"),
);
const Branches = React.lazy(
  () => import("./pages/admin/Branches"),
);
const Roles = React.lazy(
  () => import("./pages/admin/Roles"),
);
const Users = React.lazy(
  () => import("./pages/admin/Users"),
);
const Notifications = React.lazy(
  () => import("./pages/admin/Notifications"),
);
const Bottles = React.lazy(
  () => import("./pages/admin/Bottles"),
);
const Expenses = React.lazy(
  () => import("./pages/admin/Expenses"),
);
const Products = React.lazy(
  () => import("./pages/admin/Products"),
);
const Procurement = React.lazy(
  () => import("./pages/admin/Procurement"),
);
const Suppliers = React.lazy(
  () => import("./pages/admin/Suppliers"),
);
const Settings = React.lazy(
  () => import("./pages/admin/Settings"),
);
const ActivityLogs = React.lazy(
  () => import("./pages/admin/ActivityLogs"),
);
const Sales = React.lazy(
  () => import("./pages/admin/Sales"),
);

const isDriverUser = (user) =>
  String(user?.role_name || "")
    .trim()
    .toLowerCase() === "driver";

const getDefaultPath = (user) => {
  if (!user) return "/login";
  return isDriverUser(user) ? "/delivery" : "/dashboard";
};

function PrivateRoute({ children, module, action = "view" }) {
  const { user, can } = useAuth();

  if (!user) return <Navigate to="/login" replace />;

  const defaultPath = getDefaultPath(user);

  if (module && !can(module, action)) {
    return <Navigate to={defaultPath} replace />;
  }

  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  const { user } = useAuth();
  const defaultPath = getDefaultPath(user);

  return (
    <React.Suspense
      fallback={
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            fontSize: 14,
            color: "#64748b",
          }}
        >
          Loading...
        </div>
      }
    >
      <Routes>
        <Route
          path="/login"
          element={
            user ? (
              <Navigate to={defaultPath} replace />
            ) : (
              <Login />
            )
          }
        />

        <Route
          path="/dashboard"
          element={
            <PrivateRoute module="dashboard">
              <Dashboard />
            </PrivateRoute>
          }
        />

        <Route
          path="/production"
          element={
            <PrivateRoute module="production">
              <Production />
            </PrivateRoute>
          }
        />

        <Route
          path="/costs"
          element={
            <PrivateRoute module="costs">
              <CostLines />
            </PrivateRoute>
          }
        />

        <Route
          path="/pricing"
          element={
            <PrivateRoute module="pricing">
              <Pricing />
            </PrivateRoute>
          }
        />

        <Route
          path="/inventory"
          element={
            <PrivateRoute module="inventory">
              <Inventory />
            </PrivateRoute>
          }
        />

        <Route
          path="/orders"
          element={
            <PrivateRoute module="orders">
              <Orders />
            </PrivateRoute>
          }
        />

        <Route
          path="/customers"
          element={
            <PrivateRoute module="customers">
              <Customers />
            </PrivateRoute>
          }
        />

        <Route
          path="/delivery"
          element={
            <PrivateRoute module="delivery">
              <Delivery />
            </PrivateRoute>
          }
        />

        <Route
          path="/reports"
          element={
            <PrivateRoute module="reports">
              <Reports />
            </PrivateRoute>
          }
        />

        <Route
          path="/branches"
          element={
            <PrivateRoute module="branches">
              <Branches />
            </PrivateRoute>
          }
        />

        <Route
          path="/roles"
          element={
            <PrivateRoute module="roles">
              <Roles />
            </PrivateRoute>
          }
        />

        <Route
          path="/users"
          element={
            <PrivateRoute module="users">
              <Users />
            </PrivateRoute>
          }
        />

        <Route
          path="/notifications"
          element={
            <PrivateRoute>
              <Notifications />
            </PrivateRoute>
          }
        />

        <Route
          path="/bottles"
          element={
            <PrivateRoute module="bottles">
              <Bottles />
            </PrivateRoute>
          }
        />

        <Route
          path="/expenses"
          element={
            <PrivateRoute module="expenses">
              <Expenses />
            </PrivateRoute>
          }
        />

        <Route
          path="/products"
          element={
            <PrivateRoute module="products">
              <Products />
            </PrivateRoute>
          }
        />

        <Route
          path="/procurement"
          element={
            <PrivateRoute module="procurement">
              <Procurement />
            </PrivateRoute>
          }
        />

        <Route
          path="/suppliers"
          element={
            <PrivateRoute module="suppliers">
              <Suppliers />
            </PrivateRoute>
          }
        />

        <Route
          path="/settings"
          element={
            <PrivateRoute module="settings">
              <Settings />
            </PrivateRoute>
          }
        />

        <Route
          path="/activity-logs"
          element={
            <PrivateRoute>
              <ActivityLogs />
            </PrivateRoute>
          }
        />

        <Route
          path="/sales"
          element={
            <PrivateRoute module="sales">
              <Sales />
            </PrivateRoute>
          }
        />

        <Route
          path="/"
          element={
            <Navigate to={defaultPath} replace />
          }
        />

        <Route
          path="*"
          element={
            <Navigate to={defaultPath} replace />
          }
        />
      </Routes>
    </React.Suspense>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <DateRangeProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AppRoutes />
        </BrowserRouter>
      </DateRangeProvider>
    </AuthProvider>
  );
}